import React from 'react';
import { httpClient } from "util/Api";
import { config } from 'util/config';
import * as globalFunction from 'util/GlobalFunction';
import { message, notification } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined, CloseCircleFilled, MinusCircleOutlined } from '@ant-design/icons';

const propertyReportProgressTimers = new Map();
const propertyReportActiveStages = new Map();
const propertyReportProgressStates = new Map();
const propertyReportProgressMessages = new Map();
const manuallyClosedProgressKeys = new Set();

// 旧配置名保留为兼容入口，实际统一走按 configParameter 选择报告的函数。
export function downloadVIC_PropertyParcelSearch({ pageComponent } = {}) {
  return downloadVIC_PropertyReports({ pageComponent, configParameter: ['Property'] });
}

export function downloadVIC_PlanningParcelSearch({ pageComponent } = {}) {
  return downloadVIC_PropertyReports({ pageComponent, configParameter: ['Planning'] });
}

export function downloadVIC_PropertyAndPlanningReports({ pageComponent } = {}) {
  return downloadVIC_PropertyReports({ pageComponent, configParameter: ['Property', 'Planning'] });
}

// Dropdown 配置将括号中的值作为 configParameter 数组传入，可下载一份或多份 VIC 报告。
export function downloadVIC_PropertyReports({ pageComponent, configParameter } = {}) {
  const requestedTypes = Array.isArray(configParameter) ? configParameter : [];
  const reportTypeMap = { property: 'Property', planning: 'Planning' };
  const normalizedTypes = requestedTypes.map(type => {
    const key = String(type).trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(reportTypeMap, key) ? reportTypeMap[key] : null;
  });
  const reportTypes = [...new Set(normalizedTypes.filter(Boolean))];
  if (reportTypes.length === 0 || normalizedTypes.some(type => type === null)) {
    message.error('The configured VIC report type is invalid. Use Property and/or Planning.');
    return;
  }

  const isBoth = reportTypes.length === 2;
  const reportTitle = isBoth ? 'Property and Planning Reports' : `${reportTypes[0]}${reportTypes[0] === 'Planning' ? ' Property' : ''} Report`;
  const reportLabel = isBoth ? 'Property and Planning Reports' : reportTitle;
  const savedVerb = isBoth ? 'have' : 'has';
  return runPropertyReportDownload(pageComponent, {
    endpoint: 'Business/PropertyReport/DownloadVIC_PropertyAndPlanningReports',
    keyPrefix: `vic-${reportTypes.map(type => type.toLowerCase()).join('-')}-reports`,
    automationName: 'vic-property-planning-parcel',
    reportTypes,
    title: `VIC ${reportTitle}`,
    successMessage: `Victorian ${reportLabel} ${savedVerb} been saved to Documents.`,
    progressEndpoint: 'Business/PropertyReport/GetAutomationProgress',
    stages: [
      'Searching the property address',
      'Selecting the closest property match',
      isBoth ? 'Preparing and downloading both PDFs' : `Preparing and downloading the ${reportTypes[0]} PDF`,
      isBoth ? 'Saving both reports to Job Documents' : 'Saving the report to Job Documents'
    ]
  });
}

// 配置 onClick="downloadQLD_DAMSReport" 时，只调用 QLD DAMS 自动化流程。
export function downloadQLD_DAMSReport({ pageComponent } = {}) {
  return runPropertyReportDownload(pageComponent, {
    endpoint: 'Business/PropertyReport/DownloadQLD_DAMSReport',
    keyPrefix: 'qld-dams-report',
    automationName: 'qld-dams',
    title: 'QLD DAMS Report',
    successMessage: 'Queensland DAMS Report has been saved to Documents.',
    progressEndpoint: 'Business/PropertyReport/GetAutomationProgress',
    stages: [
      'Opening the DAMS website',
      'Filling in the lot and plan',
      'Simulating a click on Search',
      'Opening Reports and waiting for generation',
      'Downloading and saving the report'
    ]
  });
}

// 配置 onClick="downloadQLD_SPPReport" 时，生成 Queensland SPP Transport Infrastructure 地图。
export function downloadQLD_SPPReport({ pageComponent } = {}) {
  return runPropertyReportDownload(pageComponent, {
    endpoint: 'Business/PropertyReport/DownloadQLD_SPPReport',
    keyPrefix: 'qld-spp-report',
    automationName: 'qld-spp',
    title: 'QLD SPP Report',
    successMessage: 'Queensland SPP Report has been saved to Documents.',
    progressEndpoint: 'Business/PropertyReport/GetAutomationProgress',
    stages: [
      'Opening the Queensland SPP website',
      'Searching the property lot and plan',
      'Zooming to the selected property',
      'Selecting Transport Infrastructure layers',
      'Exporting and saving the A3 landscape map'
    ]
  });
}

// 配置 onClick="downloadQLD_GlobeReport" 时，截取 Queensland Globe 的地块及地役权图层。
export function downloadQLD_GlobeReport({ pageComponent } = {}) {
  return runPropertyReportDownload(pageComponent, {
    endpoint: 'Business/PropertyReport/DownloadQLD_GlobeReport',
    keyPrefix: 'qld-globe-report',
    automationName: 'qld-globe',
    title: 'QLD Globe',
    successMessage: 'QLD Globe image has been saved to Documents.',
    progressEndpoint: 'Business/PropertyReport/GetAutomationProgress',
    stages: [
      'Opening Queensland Globe and accepting the welcome notice',
      'Searching the property Lot on plan',
      'Selecting the property on the map',
      'Adding Land parcel and Easement layers',
      'Waiting for the map to render',
      'Saving the QLD Globe image to Documents'
    ]
  });
}

// 配置 onClick="downloadQLD_BYDAReport" 时，以 Job 地址建立 BYDA Private property enquiry。
export function downloadQLD_BYDAReport({ pageComponent } = {}) {
  return runPropertyReportDownload(pageComponent, {
    endpoint: 'Business/PropertyReport/DownloadQLD_BYDAReport',
    keyPrefix: 'qld-byda-report',
    automationName: 'qld-byda',
    title: 'QLD BYDA Report',
    successMessage: 'BYDA referral PDFs have been saved to Documents.',
    successFooter: 'Referral PDFs downloaded and saved to Documents.',
    progressEndpoint: 'Business/PropertyReport/GetAutomationProgress',
    // “等待 authority responses” 阶段保持五分钟，其余阶段沿用短步骤反馈。
    stageDurations: [
      2500, 4000, 2500, 2500, 3000, 2500,
      1500, 1500, 1500, 1500, 1500, 2500,
      2000, 5000, 300000, 300000, 5000, 3000
    ],
    stages: [
      'Opening the BYDA Portal',
      'Signing in and opening SmarterWX',
      'Searching and selecting the Job address',
      'Confirming the map job site and Private property',
      'Setting dates, Design and Planning & Design',
      'Selecting Private authority and submitting the enquiry',
      'Waiting at least 5 minutes for authority responses',
      'Opening responses and downloading the ZIP',
      'Saving referral PDFs to Job Documents'
    ]
  });
}

export function downloadQLD_PDOnlinePropertyReport({ pageComponent } = {}) {
  const developmentiUrl = pageComponent?.state?.jobData?.Lga?.DevelopmentiUrl;
  return runPropertyReportDownload(pageComponent, {
    endpoint: 'Business/PropertyReport/DownloadQLD_PDOnlinePropertyReport',
    keyPrefix: 'qld-pd-online-property-report',
    automationName: 'qld-pd-online-property-report',
    title: 'Development.i Report',
    successMessage: 'Available Development.i report documents have been saved to Documents.',
    developmentiUrl,
    progressEndpoint: 'Business/PropertyReport/GetAutomationProgress',
    stages: [
      'Opening Development.i',
      'Searching and selecting the first property address result',
      'Opening Property Details and preparing PD Online',
      'Saving PD Online to Job Documents',
      'Loading any available Property Report',
      'Saving report documents to Job Documents'
    ]
  });
}

function runPropertyReportDownload(pageComponent, options) {
  const jobId = pageComponent?.state?.jobData?.JobId || pageComponent?.props?.jobId;
  if (!jobId) {
    message.error('The current Job could not be identified. Please refresh the page and try again.');
    return;
  }

  const progressKey = `${options.keyPrefix}-${jobId}`;
  const operationId = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  startPropertyReportProgress(
    progressKey,
    options.title,
    options.stages,
    options.stageDurations,
    options.progressEndpoint,
    jobId,
    operationId,
    options.automationName);
  pageComponent.setState({ loading: true });

  return httpClient
    .post(options.endpoint, {
      JobId: jobId,
      OperationId: operationId,
      AutomationName: options.automationName,
      // 后端会从已授权 Job 的 LGA 重新读取并覆盖该 URL，避免信任浏览器传入值。
      DevelopmentiUrl: options.developmentiUrl,
      ReportTypes: options.reportTypes
    })
    .then(({ data }) => {
      if (data.result) {
        const responseData = data?.data || {};
        const isAsync = responseData?.Async === true || responseData?.async === true;
        const finish = () => {
          completePropertyReportProgress(progressKey, options.title, options.stages, options.successFooter);
          message.success({ content: options.successMessage, key: progressKey, duration: 4 });
          if (options.refreshDocuments !== false) refreshJobDocumentSection(pageComponent);
        };

        // HandlerAPI 快速返回任务编号；真正完成以进度接口的 completed 状态为准。
        if (isAsync) return waitForRemoteAutomation(progressKey).then(finish);
        finish();
      } else {
        const errorMessage = getPropertyReportErrorMessage(data);
        failPropertyReportProgress(progressKey, options.title, options.stages, errorMessage);
        message.error({ content: errorMessage, key: progressKey, duration: 6 });
      }
    })
    .catch((error) => {
      const errorMessage = getPropertyReportErrorMessage(error);
      // 下载失败仅显示提示并恢复页面状态，避免通用错误处理影响 Job Detail 页面。
      failPropertyReportProgress(progressKey, options.title, options.stages, errorMessage);
      message.error({ content: errorMessage, key: progressKey, duration: 6 });
    })
    .finally(() => {
      pageComponent?.setState?.({ loading: false });
    });
}

function startPropertyReportProgress(
  progressKey,
  title,
  stages,
  stageDurations = [],
  progressEndpoint = null,
  jobId = 0,
  operationId = '',
  automationName = '') {
  clearPropertyReportProgressTimer(progressKey);
  manuallyClosedProgressKeys.delete(progressKey);
  propertyReportProgressStates.set(progressKey, 'pending');
  propertyReportProgressMessages.set(progressKey, 'Waiting for the automation to start');
  let activeStage = 0;
  propertyReportActiveStages.set(progressKey, activeStage);
  renderPropertyReportProgress(progressKey, title, stages, activeStage);

  // 本地短流程沿用原有阶段动画；远程模式只显示 HandlerAPI 返回的真实节点。
  let usingSimulatedProgress = false;
  const scheduleNextStage = () => {
    if (activeStage >= stages.length - 1) {
      clearPropertyReportProgressTimer(progressKey);
      return;
    }
    const delay = stageDurations[activeStage] || 2200;
    const timer = window.setTimeout(() => {
      activeStage += 1;
      propertyReportActiveStages.set(progressKey, activeStage);
      renderPropertyReportProgress(progressKey, title, stages, activeStage);
      scheduleNextStage();
    }, delay);
    propertyReportProgressTimers.set(progressKey, timer);
  };
  const startSimulatedProgress = () => {
    if (usingSimulatedProgress) return;
    usingSimulatedProgress = true;
    clearPropertyReportProgressTimer(progressKey);
    propertyReportProgressMessages.delete(progressKey);
    scheduleNextStage();
  };

  // BYDA 只显示后端真实步骤；GotoAsync 未返回时始终停在 Opening Portal。
  if (progressEndpoint) {
    let polling = false;
    const pollProgress = () => {
      if (polling) return;
      polling = true;
      httpClient
        .post(progressEndpoint, { JobId: jobId, OperationId: operationId, AutomationName: automationName })
        .then(({ data }) => {
          const progress = data?.result ? data.data : null;
          const stageIndex = progress?.stageIndex ?? progress?.StageIndex;
          const status = progress?.status ?? progress?.Status;
          const progressMessage = progress?.message ?? progress?.Message;
          const mode = progress?.mode ?? progress?.Mode;
          if (mode === 'local' && automationName !== 'qld-byda') {
            startSimulatedProgress();
            return;
          }
          if (typeof status === 'string') propertyReportProgressStates.set(progressKey, status.toLowerCase());
          if (typeof progressMessage === 'string' && progressMessage.trim()) {
            propertyReportProgressMessages.set(progressKey, progressMessage);
          }
          if (Number.isInteger(stageIndex)) {
            activeStage = Math.max(0, Math.min(stageIndex, stages.length - 1));
            propertyReportActiveStages.set(progressKey, activeStage);
            renderPropertyReportProgress(progressKey, title, stages, activeStage);
          }
        })
        .catch(() => {
          // 主下载请求负责显示最终错误；短暂轮询失败时不伪造或推进步骤。
        })
        .finally(() => {
          polling = false;
        });
    };
    pollProgress();
    const timer = window.setInterval(pollProgress, 1500);
    propertyReportProgressTimers.set(progressKey, timer);
    return;
  }

  // 后端为一次长请求；按配置时长推进，最后一项保持处理中，直到 API 返回真实结果。
  startSimulatedProgress();
}

function completePropertyReportProgress(progressKey, title, stages, successFooter = 'Report saved to Documents.') {
  clearPropertyReportProgressTimer(progressKey);
  propertyReportActiveStages.delete(progressKey);
  propertyReportProgressStates.delete(progressKey);
  propertyReportProgressMessages.delete(progressKey);
  // 下载成功后短暂展示完成状态，然后自动关闭整个进度通知。
  renderPropertyReportProgress(progressKey, title, stages, stages.length, null, successFooter, -1, 4);
}

function failPropertyReportProgress(progressKey, title, stages, errorMessage) {
  clearPropertyReportProgressTimer(progressKey);
  const failedStage = propertyReportActiveStages.get(progressKey) ?? 0;
  propertyReportActiveStages.delete(progressKey);
  propertyReportProgressStates.delete(progressKey);
  propertyReportProgressMessages.delete(progressKey);
  renderPropertyReportProgress(progressKey, title, stages, -1, errorMessage, null, failedStage);
}

function waitForRemoteAutomation(progressKey) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const status = propertyReportProgressStates.get(progressKey);
      if (status === 'completed') {
        window.clearInterval(timer);
        resolve();
      } else if (status === 'failed') {
        window.clearInterval(timer);
        reject(new Error(propertyReportProgressMessages.get(progressKey) || 'Remote automation failed.'));
      } else if (Date.now() - startedAt > 30 * 60 * 1000) {
        window.clearInterval(timer);
        reject(new Error('Remote automation timed out after 30 minutes.'));
      }
    }, 500);
  });
}

function clearPropertyReportProgressTimer(progressKey) {
  const timer = propertyReportProgressTimers.get(progressKey);
  if (timer) {
    window.clearTimeout(timer);
    window.clearInterval(timer);
  }
  propertyReportProgressTimers.delete(progressKey);
}

function renderPropertyReportProgress(
  progressKey,
  title,
  stages,
  activeStage,
  errorMessage = null,
  footer = null,
  failedStage = -1,
  notificationDuration = 0) {
  // 用户手动关闭后，后台下载继续执行，但不再重新弹出面板。
  if (manuallyClosedProgressKeys.has(progressKey)) return;

  notification.open({
    key: progressKey,
    message: title,
    description: React.createElement(PropertyReportProgress, {
      stages,
      activeStage,
      errorMessage,
      footer,
      failedStage,
      currentMessage: propertyReportProgressMessages.get(progressKey)
    }),
    placement: 'topRight',
    duration: notificationDuration,
    onClose: () => {
      manuallyClosedProgressKeys.add(progressKey);
      // 用户关闭面板后仍继续后台轮询，否则远程任务完成状态无法被页面接收和刷新 Documents。
    }
  });
}

function PropertyReportProgress({ stages, activeStage, errorMessage, footer, failedStage, currentMessage }) {
  return React.createElement('div', null,
    stages.map((stage, index) => {
      const completed = activeStage > index || activeStage === stages.length;
      const failed = Boolean(errorMessage) && index === failedStage;
      const icon = completed
        ? React.createElement(CheckCircleFilled, { style: { color: '#52c41a', marginRight: 8 } })
        : failed
          ? React.createElement(CloseCircleFilled, { style: { color: '#ff4d4f', marginRight: 8 } })
          : index === activeStage
            ? React.createElement(ClockCircleOutlined, { style: { color: '#1890ff', marginRight: 8 } })
            : React.createElement(MinusCircleOutlined, { style: { color: '#bfbfbf', marginRight: 8 } });
      return React.createElement('div', { key: stage, style: { display: 'flex', alignItems: 'center', marginBottom: 7 } }, icon, stage);
    }),
    !errorMessage && !footer && currentMessage && React.createElement('div', { style: { color: '#1890ff', marginTop: 8 } }, currentMessage),
    errorMessage && React.createElement('div', { style: { color: '#ff4d4f', marginTop: 8 } }, errorMessage),
    footer && React.createElement('div', { style: { color: '#52c41a', marginTop: 8 } }, footer)
  );
}

function getPropertyReportErrorMessage(source) {
  const responseData = source?.response?.data;
  const apiMessage = responseData?.message
    || (typeof responseData === 'string' ? responseData : null)
    || source?.message;

  return typeof apiMessage === 'string' && apiMessage.trim()
    ? apiMessage
    : 'Property report download failed. Please try again later.';
}

// Job Detail 既支持普通 Section，也支持 Tab Section；下载完成后刷新 Documents 列表。
function refreshJobDocumentSection(pageComponent) {
  const createPage = pageComponent?.createPage?.current;
  const page = createPage?.state?.page;
  const documentIndex = page?.sectionList?.findIndex(section => section.name === 'Document');
  if (documentIndex === undefined || documentIndex < 0) return;

  if (page.type === 'Tab') {
    createPage?.child?.[0]?.current?.child?.[documentIndex]?.current?.getSectionDataByDefault();
  } else {
    createPage?.child?.[documentIndex]?.current?.getSectionDataByDefault();
  }
}

export function getLgaDataSource({ sectionName, parameter, sectionComponent, pageComponent, uie } = {}) {
  if (globalFunction.isNullOrEmpty(parameter)) {
    parameter = pageComponent.state.jobData.PptState;
  }
  let that = this;
  httpClient
    .get('Admin/Lga/GetSelectOptionList?state=' + parameter)
    .then(({ data }) => {
      if (data.result) {
        if (data.data.length > 0) {
          let componentDataSource = sectionComponent.customForm.current?.state.componentDataSource;
          componentDataSource.LgaId = data.data;
          componentDataSource.PptSuburb = [];
          sectionComponent.customForm.current?.setState({ componentDataSource });
          if (data.data.map(item => { return item.Value }).indexOf(sectionComponent.customForm.current?.formRef.current?.getFieldValue('LgaId')) < 0) {
            sectionComponent.customForm.current?.formRef.current?.setFieldsValue({ LgaId: 0 });
          }
        }
      } else {
        globalFunction.handleResultError(data, this.props.pageComponent);
      }
    })
    .catch(function (error) {
      globalFunction.handleError(error, that.props.pageComponent);
    });
}

export function openMap({ sectionName, parameter, sectionComponent, pageComponent, uie } = {}) {
  pageComponent.setState({ mapShow: true });
}

export function openLandData({ sectionName, parameter, sectionComponent, pageComponent, uie } = {}) {
  window.open(uie.href);
}

//Suburb控件：查找自动 Suburb 和 Postcode
export function searchSuburbChange({ sectionName, parameter, formComponent, sectionComponent, pageComponent, uie }) {
  if (parameter.length >= config.component.AutoComplete.minimumQueryCharacters && !uie.autoDataSource && uie.datasourceUrl !== null) {
    let state = formComponent.formRef.current?.getFieldValue('PptState');
    if (state === null || state === undefined) {
      state = pageComponent.state.jobData.ModuleConfig.DefaultState;
    }
    httpClient
      .get(uie.datasourceUrl + '?keyword=' + parameter + '&state=' + state)
      .then(({ data }) => {
        let options = [];
        if (data.result) {
          data.data.map((suburb, j) => {
            options.push({ label: suburb.Name + ' (' + suburb.Postcode + ')', value: suburb.Name + ' (' + suburb.Postcode + ')', data: suburb });
          })
          let componentDataSource = { ...formComponent.state.componentDataSource };
          componentDataSource[uie.source] = options;
          formComponent.setState({ componentDataSource });
        } else {
          globalFunction.handleResultError(data, pageComponent);
        }
      })
      .catch(function (error) {
        globalFunction.handleError(error, pageComponent);
      });
  }
}

//Suburb 选择后快速填写数据
export function searchSuburbSelect({ sectionName, parameter, formComponent, sectionComponent, pageComponent, uie }) {
  let data = parameter;
  formComponent.formRef.current?.setFieldsValue({ PptSuburb: data.data.Name, PptPostcode: data.data.Postcode });
  if (data.data.LgaId !== null) {
    formComponent.formRef.current?.setFieldsValue({ LgaId: data.data.LgaId });
  }
}
